import { useMemo, useState, type ReactNode } from "react";
import { format } from "date-fns";
import { AlertCircle, CalendarDays, Check, MessageCircle, Timer, Trophy, type LucideIcon } from "lucide-react";
import { Button, Card, Chip, EmptyState, NumberBox, Page, PageHeader } from "@/components/ds";
import * as AlertDialogPrimitive from "@radix-ui/react-alert-dialog";
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { usePageCrumbs } from "@/components/layout/breadcrumbs";
import usePermissions from "@/hooks/usePermissions";
import {
  isValidationError,
  previewErrorMessage,
  useEngagementPreview,
  useEngagementSettings,
  useEngagementSettingsHistory,
  useSaveEngagementSettings,
} from "@/hooks/useEngagementSettings";
import {
  MESSAGES,
  WEIGHT_FIELDS,
  exampleParts,
  historySummary,
  isDirty,
  nextUpdateLabel,
  partsMatchTotal,
  tierFor,
  toBody,
  toForm,
  validate,
  weightFractions,
  weightsTotal,
  type EngagementForm,
  type FormField,
  type Tier,
} from "@/lib/engagementFormula";
import { cn } from "@/lib/utils";
import type {
  EngagementScoringSettingsHistoryResponse,
  EngagementScoringSettingsRequest,
  EngagementScoringSettingsResponse,
} from "@/types/generated/cms.generated";

const WEIGHT_ROWS: Record<(typeof WEIGHT_FIELDS)[number]["field"], { icon: LucideIcon; description: string }> = {
  challenge: { icon: Trophy, description: "Joining, finishing, daily streaks, ranking" },
  activity: { icon: CalendarDays, description: "Replying, saying yes, checking in" },
  consistency: { icon: Timer, description: "Days in the app, time spent, daily streak" },
  social: { icon: MessageCircle, description: "Feed, kudos, announcements, photo, feedback" },
};

/** Level swatches: four neutral ds tones from light to dark (the ds token set has no tier colours). */
const LEVEL_SWATCH: Record<Tier, string> = {
  Bronze: "bg-ds-sunk border border-ds-line2",
  Silver: "bg-ds-line2",
  Gold: "bg-ds-ink3",
  Platinum: "bg-ds-ink2",
};

/** First History rows shown before "Show all". */
const HISTORY_PAGE_SIZE = 10;

const WEIGHT_MESSAGE_PREFIX = "Weights must add up";
const count = (n: number | undefined) => (n === undefined ? "–" : n.toLocaleString("en-US"));
const oneDecimal = (n: number) => (Math.round(n * 10) / 10).toString();

/** Engagement score — the one formula for every company. Admins edit; the Athlyn team reads. */
export default function EngagementScorePage() {
  usePageCrumbs([{ label: "Engagement score" }]);
  const { isAthlynAdmin } = usePermissions();
  const settings = useEngagementSettings();

  const header = (actions?: ReactNode) => (
    <PageHeader
      title="Engagement score"
      subtitle={
        'One formula for every company. Employees see these numbers in the app\'s "How your score works". Changes apply at the next nightly update (7:00 AM UAE).'
      }
      actions={actions}
    />
  );

  if (settings.isLoading) {
    return (
      <Page data-testid="engagement-score-page">
        {header()}
        <div className="grid gap-4 lg:grid-cols-[1fr_320px]" data-testid="engagement-loading" aria-busy="true">
          <div className="space-y-4">
            {[0, 1, 2].map((i) => (
              <Card key={i} className="h-32 animate-pulse" />
            ))}
          </div>
          <div className="space-y-4">
            {[0, 1].map((i) => (
              <Card key={i} className="h-28 animate-pulse" />
            ))}
          </div>
        </div>
      </Page>
    );
  }

  if (settings.isError || !settings.data) {
    return (
      <Page data-testid="engagement-score-page">
        {header()}
        <Card>
          <EmptyState
            icon={AlertCircle}
            title="Couldn't load the formula"
            text="Something went wrong while loading the engagement score settings."
            action={{ label: "Try again", onClick: () => void settings.refetch() }}
          />
        </Card>
      </Page>
    );
  }

  return (
    <FormulaEditor
      key={JSON.stringify(settings.data)}
      original={settings.data}
      canEdit={isAthlynAdmin}
      header={header}
    />
  );
}

interface FormulaEditorProps {
  original: EngagementScoringSettingsResponse;
  canEdit: boolean;
  header: (actions?: ReactNode) => ReactNode;
}

function FormulaEditor({ original, canEdit, header }: FormulaEditorProps) {
  const [form, setForm] = useState<EngagementForm>(() => toForm(original));
  const [confirmOpen, setConfirmOpen] = useState(false);
  const save = useSaveEngagementSettings();

  const errors = useMemo(() => validate(form, original), [form, original]);
  const valid = errors.length === 0;
  const dirty = isDirty(form, original);
  const body = useMemo<EngagementScoringSettingsRequest | null>(() => {
    if (!valid) return null;
    try {
      return toBody(form, original);
    } catch {
      return null;
    }
  }, [form, original, valid]);
  const preview = useEngagementPreview(body, body !== null);

  const setField = (field: FormField) => (value: string) => setForm((prev) => ({ ...prev, [field]: value }));
  const box = (field: FormField, label: string, suffix?: string, width?: number) => (
    <NumberBox
      value={form[field]}
      onChange={canEdit ? setField(field) : undefined}
      readOnly={!canEdit}
      label={label}
      suffix={suffix}
      width={width}
    />
  );

  const total = weightsTotal(form, original);
  const weightErrors = errors.filter((e) => e === MESSAGES.weightRange || e.startsWith(WEIGHT_MESSAGE_PREFIX));
  const fractions = weightFractions(form, original);
  const previewData = valid ? preview.data : undefined;
  // Reach and level-change numbers are only ever those of the body on screen (never a previous body's).
  const previewReady = valid && preview.isCurrent;
  // The server refused or failed the preview of the body on screen: its reason is shown and Save stays off.
  const previewFailed = valid && preview.isCurrentError;
  const previewError = previewFailed ? previewErrorMessage(preview.failure) : null;
  const readyData = previewReady ? preview.data : undefined;

  const onConfirm = () => {
    if (!body || save.isPending) return;
    save.mutate(body, { onSettled: () => setConfirmOpen(false) });
  };

  const actions = canEdit ? (
    <>
      <Button variant="secondary" disabled={!dirty || save.isPending} onClick={() => setForm(toForm(original))}>
        Discard
      </Button>
      {/* The dialog's Trigger: Radix returns focus here when the confirm closes. */}
      <AlertDialogTrigger asChild>
        {/* Waits for the current body's preview (so the confirm never names "– employees"); off when it failed. */}
        <Button variant="primary" disabled={!dirty || !valid || body === null || !previewReady} loading={save.isPending}>
          Save changes
        </Button>
      </AlertDialogTrigger>
    </>
  ) : (
    <Chip tone="draft">View only · admins can edit</Chip>
  );

  return (
    // The AlertDialog root wraps the page so the header's Save changes button can be its Trigger.
    <AlertDialog open={confirmOpen} onOpenChange={(open) => !save.isPending && setConfirmOpen(open)}>
      <Page data-testid="engagement-score-page">
        {header(actions)}
        <div className="grid gap-4 lg:grid-cols-[1fr_320px]">
          <div className="min-w-0 space-y-4">
            {/* Weights */}
            <Card className="px-4 py-3.5" data-testid="weights-card">
              <div className="flex items-center justify-between gap-2">
                <h2 className="text-[14px] font-semibold text-ds-ink">Weights</h2>
                {total === 100 && !errors.includes(MESSAGES.weightRange) ? (
                  <Chip tone="ok">
                    <Check className="h-3 w-3" aria-hidden="true" />
                    Total 100%
                  </Chip>
                ) : (
                  <Chip tone="err">Total {total === null ? "–" : oneDecimal(total)}%</Chip>
                )}
              </div>
              <table className="mt-1.5 w-full border-collapse text-[13.5px]">
                <tbody>
                  {WEIGHT_FIELDS.map(({ field, label }, i) => {
                    const { icon: Icon, description } = WEIGHT_ROWS[field];
                    const pct = Math.max(0, Math.min(100, fractions[i] * 100));
                    return (
                      <tr key={field} className="border-b border-ds-line last:border-b-0">
                        <td className="w-9 py-2.5 pr-2 align-middle">
                          <span className="grid h-7 w-7 place-items-center rounded-lg bg-ds-sunk text-ds-ink2">
                            <Icon className="h-[15px] w-[15px]" aria-hidden="true" />
                          </span>
                        </td>
                        <td className="py-2.5 pr-3 align-middle">
                          <span className="font-semibold text-ds-ink">{label}</span>
                          <span className="block text-[12.5px] text-ds-ink3">{description}</span>
                        </td>
                        <td className="hidden w-[180px] py-2.5 pr-3 align-middle sm:table-cell">
                          <span className="block h-1.5 w-full overflow-hidden rounded-full bg-ds-sunk" aria-hidden="true">
                            <span className="block h-full rounded-full bg-ds-accent" style={{ width: `${pct}%` }} />
                          </span>
                        </td>
                        <td className="w-[90px] py-2.5 text-right align-middle">{box(field, `${label} weight`, "%")}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
              <FieldErrors messages={weightErrors} testId="weights-errors" />
            </Card>

            {/* Levels */}
            <Card className="px-4 py-3.5" data-testid="levels-card">
              <h2 className="text-[14px] font-semibold text-ds-ink">Levels</h2>
              <p className="mb-2.5 mt-0.5 text-[13.5px] text-ds-ink2">Based on the all-time score.</p>
              <div className="grid grid-cols-2 gap-2.5 sm:grid-cols-4">
                <LevelBox tier="Bronze">under {form.silver.trim() || "–"}</LevelBox>
                <LevelBox tier="Silver">from {box("silver", "Silver from", undefined, 54)}</LevelBox>
                <LevelBox tier="Gold">from {box("gold", "Gold from", undefined, 54)}</LevelBox>
                <LevelBox tier="Platinum">from {box("platinum", "Platinum from", undefined, 54)}</LevelBox>
              </div>
              <FieldErrors messages={errors.filter((e) => e === MESSAGES.levels)} testId="levels-errors" />
            </Card>

            {/* When a score slips */}
            <Card className="px-4 py-3.5" data-testid="slip-card">
              <h2 className="text-[14px] font-semibold text-ds-ink">When a score slips</h2>
              <p className="mt-1.5 text-[13.5px] leading-[2.1] text-ds-ink2">
                Start lowering the score after {box("graceDays", "Days without activity before the score lowers")} days
                without activity, reach the lowest point after {box("windowDays", "Days to reach the lowest point")} more
                days, never below {box("floor", "Lowest point", "%")} of the score.
              </p>
              <FieldErrors
                messages={errors.filter((e) => e === MESSAGES.grace || e === MESSAGES.window || e === MESSAGES.floor)}
                testId="slip-errors"
              />
            </Card>

            <HistoryCard />
          </div>

          <div className="space-y-4">
            {/* Preview */}
            <Card className="px-4 py-3.5" data-testid="preview-card">
              <h2 className="text-[13px] font-semibold text-ds-ink">Preview</h2>
              <p className="mb-2 mt-0.5 text-[12.5px] text-ds-ink2">An employee scoring 70 · 60 · 80 · 50 in the four parts:</p>
              {previewError !== null ? (
                <PreviewError
                  message={previewError}
                  onRetry={isValidationError(preview.failure) ? undefined : () => void preview.refetch()}
                  retrying={preview.isRetrying}
                />
              ) : previewData?.exampleTotal !== undefined ? (
                <PreviewNumbers form={form} original={original} exampleTotal={previewData.exampleTotal} />
              ) : valid ? (
                <span className="block h-9 w-24 animate-pulse rounded bg-ds-sunk" aria-hidden="true" />
              ) : (
                <span className="font-ds-mono text-[30px] font-bold text-ds-ink3">–</span>
              )}
            </Card>

            {/* Facts */}
            <Card className="px-4 py-3.5" data-testid="facts-card">
              <dl className="grid grid-cols-[auto_1fr] gap-x-3 gap-y-2 text-[13px]">
                <dt className="text-ds-ink3">Applies to</dt>
                <dd className="font-semibold text-ds-ink" data-testid="applies-to">
                  {readyData
                    ? `${count(readyData.affectedEmployees)} employees · ${count(readyData.affectedOrganizations)} companies`
                    : "–"}
                </dd>
                <dt className="text-ds-ink3">Last changed</dt>
                <dd className="font-semibold text-ds-ink" data-testid="last-changed">
                  {lastChangedText(original)}
                </dd>
                <dt className="text-ds-ink3">Next update</dt>
                <dd className="font-semibold text-ds-ink">{nextUpdateLabel(new Date())}</dd>
              </dl>
            </Card>

            {/* If you save these changes */}
            {dirty && (
              <Card className="px-4 py-3.5" data-testid="level-change-card">
                <h2 className="text-[13px] font-semibold text-ds-ink">If you save these changes</h2>
                {valid && previewError !== null ? (
                  <p className="mt-1 text-[12.5px] font-medium text-ds-err" data-testid="level-change-error">
                    {previewError}
                  </p>
                ) : valid ? (
                  <p className="mt-1 text-[12.5px] text-ds-ink2">
                    Levels would change for{" "}
                    <b className="text-ds-ink">{count(readyData?.levelChanges)} employees</b>. Preview updates as you
                    edit.
                  </p>
                ) : (
                  <p className="mt-1 text-[12.5px] text-ds-ink2">Fix the numbers above to see the preview.</p>
                )}
              </Card>
            )}
          </div>
        </div>

        <AlertDialogContent className="rounded-ds-lg border-ds-line bg-ds-panel font-ds text-ds-ink shadow-ds">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-[16px] font-semibold text-ds-ink">Save the new formula?</AlertDialogTitle>
            <AlertDialogDescription className="text-[13.5px] text-ds-ink2">
              It applies to {count(readyData?.affectedEmployees)} employees at{" "}
              {count(readyData?.affectedOrganizations)} companies at the next nightly update.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            {/* The dialog's Cancel part (bare primitive, ds-styled): Radix focuses it when the confirm opens. */}
            <AlertDialogPrimitive.Cancel asChild>
              <Button variant="secondary" disabled={save.isPending}>
                Cancel
              </Button>
            </AlertDialogPrimitive.Cancel>
            {/* Never confirm on a previous body's reach: wait for the current body's preview. */}
            <Button variant="primary" disabled={!previewReady} loading={save.isPending} onClick={onConfirm}>
              Save changes
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </Page>
    </AlertDialog>
  );
}

interface PreviewErrorProps {
  message: string;
  /** Offered for failures other than a server validation refusal. */
  onRetry?: () => void;
  /** The preview is being fetched again: Try again shows its busy state. */
  retrying?: boolean;
}

function PreviewError({ message, onRetry, retrying = false }: PreviewErrorProps) {
  return (
    <div data-testid="preview-error">
      <p className="text-[12.5px] font-medium text-ds-err">{message}</p>
      {onRetry && (
        <Button className="mt-1.5" size="sm" variant="secondary" loading={retrying} onClick={onRetry}>
          Try again
        </Button>
      )}
    </div>
  );
}

function lastChangedText(original: EngagementScoringSettingsResponse): string {
  // The seeded row has never been saved: its UpdatedAt is only the seed date.
  if (!original.updatedByUserId || !original.updatedAt) return "No changes yet";
  const date = format(new Date(original.updatedAt), "d MMM");
  return original.updatedByName ? `${date} by ${original.updatedByName}` : date;
}

interface FieldErrorsProps {
  messages: string[];
  testId: string;
}

function FieldErrors({ messages, testId }: FieldErrorsProps) {
  if (messages.length === 0) return null;
  return (
    <div className="mt-2 space-y-0.5" data-testid={testId}>
      {messages.map((m) => (
        <p key={m} className="text-[12.5px] font-medium text-ds-err">
          {m}
        </p>
      ))}
    </div>
  );
}

interface LevelBoxProps {
  tier: Tier;
  children: ReactNode;
}

function LevelBox({ tier, children }: LevelBoxProps) {
  return (
    <div className="flex flex-col gap-1.5 rounded-ds border border-ds-line p-2.5" data-testid={`level-${tier}`}>
      <span className={cn("block h-[18px] w-[18px] rounded-full", LEVEL_SWATCH[tier])} aria-hidden="true" />
      <span className="text-[13.5px] font-semibold text-ds-ink">{tier}</span>
      <span className="flex items-center gap-1 text-[12.5px] text-ds-ink3">{children}</span>
    </div>
  );
}

interface PreviewNumbersProps {
  form: EngagementForm;
  original: EngagementScoringSettingsResponse;
  exampleTotal: number;
}

function PreviewNumbers({ form, original, exampleTotal }: PreviewNumbersProps) {
  const parts = exampleParts(form, original);
  return (
    <>
      <div className="flex items-baseline gap-2">
        <span className="font-ds-mono text-[30px] font-bold text-ds-ink" data-testid="preview-total">
          {exampleTotal.toFixed(1)}
        </span>
        <Chip tone="neutral">{tierFor(exampleTotal, form)}</Chip>
      </div>
      {/* The server's total wins: the local parts line shows only when it adds up to it. */}
      {partsMatchTotal(parts, exampleTotal) && (
        <p className="mt-1.5 text-[12px] text-ds-ink2" data-testid="preview-parts">
          {parts.map(oneDecimal).join(" + ")}
        </p>
      )}
    </>
  );
}

function HistoryCard() {
  const history = useEngagementSettingsHistory();
  const [showAll, setShowAll] = useState(false);
  const rows = history.data ?? [];
  const shown = showAll ? rows : rows.slice(0, HISTORY_PAGE_SIZE);

  return (
    <Card className="px-4 py-3.5" data-testid="history-card">
      <h2 className="text-[14px] font-semibold text-ds-ink">History</h2>
      {history.isLoading ? (
        <div className="mt-2 space-y-2" aria-busy="true">
          {[0, 1].map((i) => (
            <span key={i} className="block h-4 w-2/3 animate-pulse rounded bg-ds-sunk" />
          ))}
        </div>
      ) : history.isError ? (
        <div className="mt-1 flex items-center gap-2 text-[13px] text-ds-ink2">
          Couldn't load the history.
          <Button size="sm" variant="ghost" onClick={() => void history.refetch()}>
            Try again
          </Button>
        </div>
      ) : rows.length === 0 ? (
        <p className="mt-1 text-[13px] text-ds-ink2">No changes yet.</p>
      ) : (
        <>
          <ul className="mt-1.5">
            {/* `shown` is a prefix of `rows`, so the map index is the row's index in `rows`. */}
            {shown.map((row, i) => (
              <HistoryRow key={row.id ?? i} row={row} previous={rows[i + 1]} />
            ))}
          </ul>
          {!showAll && rows.length > HISTORY_PAGE_SIZE && (
            <Button className="mt-2" size="sm" variant="ghost" onClick={() => setShowAll(true)}>
              Show all
            </Button>
          )}
        </>
      )}
    </Card>
  );
}

interface HistoryRowProps {
  row: EngagementScoringSettingsHistoryResponse;
  previous?: EngagementScoringSettingsHistoryResponse;
}

function HistoryRow({ row, previous }: HistoryRowProps) {
  // The oldest row has no earlier snapshot (seeded values are never written to history): no summary.
  const summary = previous?.values && row.values ? historySummary(previous.values, row.values) : "";
  return (
    <li className="border-b border-ds-line py-2 last:border-b-0" data-testid="history-row">
      <span className="text-[13px] text-ds-ink">
        {row.changedAt ? format(new Date(row.changedAt), "d MMM yyyy, HH:mm") : "–"} ·{" "}
        {row.changedByName ?? "An Athlyn admin"}
      </span>
      {summary && (
        <span className="block text-[12.5px] text-ds-ink3" data-testid="history-summary">
          {summary}
        </span>
      )}
    </li>
  );
}
